import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISMISS_KEY = 'staleness_banner_dismissed_at';
const DISMISS_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Global banner shown when one or more watchlists are stale (yellow) or
 * failing (red). Auto-hides for 6h after user dismisses.
 * Only rendered for super_admin — org users don't manage sync.
 */
export default function StalenessBanner({ user }) {
  const [issues, setIssues] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.role !== 'super_admin') return;
    // Respect prior dismissal
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (ts && Date.now() - ts < DISMISS_TTL_MS) {
        setDismissed(true);
        return;
      }
    } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/watchlist/freshness`);
        if (cancelled) return;
        const bad = (data?.sources || []).filter(s => s.status === 'red' || s.status === 'yellow');
        setIssues(bad);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (dismissed || issues.length === 0 || user?.role !== 'super_admin') return null;

  const red = issues.filter(i => i.status === 'red');
  const yellow = issues.filter(i => i.status === 'yellow');
  const severity = red.length > 0 ? 'red' : 'yellow';

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const styles = severity === 'red'
    ? 'bg-rose-950/80 border-rose-500/40 text-rose-100'
    : 'bg-amber-950/80 border-amber-500/40 text-amber-100';
  const dotColor = severity === 'red' ? 'bg-rose-500' : 'bg-amber-500';

  return (
    <div className={`border-b ${styles}`} data-testid="staleness-banner">
      <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-sm">
          <span className={`w-2 h-2 rounded-full ${dotColor} animate-pulse flex-shrink-0`} />
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            {red.length > 0 && (
              <><strong>{red.length}</strong> watchlist{red.length !== 1 ? 's' : ''} failing: {red.map(r => r.source).join(', ')}. </>
            )}
            {yellow.length > 0 && (
              <>{red.length > 0 ? 'Also ' : ''}<strong>{yellow.length}</strong> stale: {yellow.map(y => y.source).join(', ')}. </>
            )}
            <Link to="/data" className="underline font-semibold hover:opacity-80" data-testid="staleness-banner-cta">Review now →</Link>
          </span>
        </div>
        <button onClick={dismiss} className="p-1 hover:opacity-70 flex-shrink-0" aria-label="Dismiss" data-testid="staleness-banner-dismiss">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
